import { resolveMediaUrl } from '../mediaUrl';

const isCrossOriginUrl = (url: string): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return new URL(url).origin !== window.location.origin;
  } catch {
    return false;
  }
};

const shouldFetchAsBlob = (url: string): boolean => {
  if (url.startsWith('data:') || url.startsWith('blob:')) {
    return false;
  }
  const resolved = resolveMediaUrl(url) ?? url;
  return isCrossOriginUrl(resolved);
};

export type LoadedEditableImage = {
  image: HTMLImageElement;
  /** Revoke object URLs created while loading (not caller-owned blob URLs). */
  cleanup: () => void;
};

export const loadEditableImage = async (url: string): Promise<LoadedEditableImage> => {
  const resolved = resolveMediaUrl(url) ?? url;
  let objectUrlToRevoke: string | null = null;

  const loadFromSrc = (src: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Не удалось декодировать изображение'));
      if (!src.startsWith('data:') && !src.startsWith('blob:')) {
        img.crossOrigin = 'anonymous';
      }
      img.src = src;
    });

  let src = resolved;
  if (shouldFetchAsBlob(url)) {
    try {
      const response = await fetch(resolved, { mode: 'cors', credentials: 'omit' });
      if (!response.ok) {
        throw new Error('Не удалось загрузить изображение');
      }
      const blob = await response.blob();
      objectUrlToRevoke = URL.createObjectURL(blob);
      src = objectUrlToRevoke;
    } catch {
      // Fallback: direct load with crossOrigin when fetch is blocked
      src = resolved;
    }
  }

  try {
    const image = await loadFromSrc(src);
    return {
      image,
      cleanup: () => {
        if (objectUrlToRevoke) {
          URL.revokeObjectURL(objectUrlToRevoke);
        }
      },
    };
  } catch (error) {
    if (objectUrlToRevoke) {
      URL.revokeObjectURL(objectUrlToRevoke);
    }
    throw error;
  }
};
